/**
 * Production-Ready Service Factory
 * 
 * Simple, type-safe, no dependency injection complexity
 * As recommended by the expert panel
 */

import { MongoClient, Db } from 'mongodb';
import { getConfig, PartyManagerConfig } from '../../config/environment';
import { Logger } from '../logging/Logger';
import { EventBus } from '../events/EventBus';

// Services
import { AuthenticationService } from '../../bounded-contexts/authentication/application/services/AuthenticationService';
import { RegistrationService } from '../../bounded-contexts/authentication/application/services/RegistrationService';
import { PasswordService } from '../../bounded-contexts/authentication/application/services/PasswordService';

// Repositories
import { MongoPersonAuthRepository } from '../repositories/MongoPersonAuthRepository';
import { MongoAuthSessionRepository } from '../repositories/MongoAuthSessionRepository';
import { MongoPersonRepository } from '../repositories/MongoPersonRepository';
import { MongoOrganisationRepository } from '../repositories/MongoOrganisationRepository';
import { MongoTeamRepository } from '../repositories/MongoTeamRepository';

/**
 * Service factory that manages all dependencies
 * No Inversify, no complexity, just simple factory pattern
 */

export class ServiceFactory {
  private static instance: ServiceFactory;
  
  private mongoClient?: MongoClient;
  private database?: Db;
  private config: PartyManagerConfig;
  private logger: Logger;
  private eventBus: EventBus;
  
  // Repository instances (lazy loaded)
  private personAuthRepo?: MongoPersonAuthRepository;
  private authSessionRepo?: MongoAuthSessionRepository;
  private personRepo?: MongoPersonRepository;
  private organisationRepo?: MongoOrganisationRepository;
  private teamRepo?: MongoTeamRepository;
  
  // Service instances (lazy loaded)
  private authService?: AuthenticationService;
  private registrationService?: RegistrationService;
  private passwordService?: PasswordService;

  private constructor() {
    this.config = getConfig();
    this.logger = Logger.getInstance();
    this.eventBus = EventBus.getInstance();
  }

  static getInstance(): ServiceFactory {
    if (!ServiceFactory.instance) {
      ServiceFactory.instance = new ServiceFactory();
    }
    return ServiceFactory.instance;
  }

  /**
   * Initialize database connection
   * Should be called once at application startup
   */
  async initialize(): Promise<void> {
    try {
      this.logger.info('Initializing Party Manager services');
      
      // Create MongoDB client with production settings
      this.mongoClient = new MongoClient(this.config.mongodb.url, {
        maxPoolSize: this.config.mongodb.options.maxPoolSize,
        minPoolSize: this.config.mongodb.options.minPoolSize,
        maxIdleTimeMS: this.config.mongodb.options.maxIdleTimeMS,
        connectTimeoutMS: this.config.mongodb.options.connectTimeoutMS,
        serverSelectionTimeoutMS: this.config.mongodb.options.serverSelectionTimeoutMS
      });

      await this.mongoClient.connect();
      this.database = this.mongoClient.db(this.config.mongodb.database);
      
      // Verify connection
      await this.database.admin().ping();
      
      this.logger.info('Party Manager services initialized successfully', {
        database: this.config.mongodb.database,
        poolSize: this.config.mongodb.options.maxPoolSize
      });
    } catch (error) {
      this.logger.error('Failed to initialize Party Manager services', error as Error);
      throw error;
    }
  }

  /**
   * Gracefully shutdown all connections
   */
  async shutdown(): Promise<void> {
    try {
      this.logger.info('Shutting down Party Manager services');
      
      if (this.mongoClient) {
        await this.mongoClient.close();
      }
      
      this.logger.info('Party Manager services shut down successfully');
    } catch (error) {
      this.logger.error('Error during shutdown', error as Error);
      throw error;
    }
  }

  /**
   * Get database instance
   */
  private getDatabase(): Db {
    if (!this.database) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    return this.database;
  }

  // ========================================================================
  // Repository Getters (Lazy Loading)
  // ========================================================================

  getPersonAuthRepository(): MongoPersonAuthRepository {
    if (!this.personAuthRepo) {
      this.personAuthRepo = new MongoPersonAuthRepository(
        this.config.mongodb.url,
        this.config.mongodb.database
      );
    }
    return this.personAuthRepo;
  }

  getAuthSessionRepository(): MongoAuthSessionRepository {
    if (!this.authSessionRepo) {
      this.authSessionRepo = new MongoAuthSessionRepository(
        this.config.mongodb.url,
        this.config.mongodb.database
      );
    }
    return this.authSessionRepo;
  }

  getPersonRepository(): MongoPersonRepository {
    if (!this.personRepo) {
      this.personRepo = new MongoPersonRepository(
        this.config.mongodb.url,
        this.config.mongodb.database
      );
    }
    return this.personRepo;
  }

  getOrganisationRepository(): MongoOrganisationRepository {
    if (!this.organisationRepo) {
      this.organisationRepo = new MongoOrganisationRepository(
        this.config.mongodb.url,
        this.config.mongodb.database
      );
    }
    return this.organisationRepo;
  }

  getTeamRepository(): MongoTeamRepository {
    if (!this.teamRepo) {
      this.teamRepo = new MongoTeamRepository(
        this.config.mongodb.url,
        this.config.mongodb.database
      );
    }
    return this.teamRepo;
  }

  // ========================================================================
  // Service Getters (Lazy Loading)
  // ========================================================================

  getAuthenticationService(): AuthenticationService {
    if (!this.authService) {
      this.authService = new AuthenticationService(
        this.getPersonAuthRepository(),
        this.getAuthSessionRepository(),
        this.eventBus  // Now using real EventBus
      );
    }
    return this.authService;
  }

  getRegistrationService(): RegistrationService {
    if (!this.registrationService) {
      this.registrationService = new RegistrationService(
        this.getPersonAuthRepository(),
        this.getPersonRepository(),
        this.eventBus  // Now using real EventBus
      );
    }
    return this.registrationService;
  }

  getPasswordService(): PasswordService {
    if (!this.passwordService) {
      const emailService = {
        send: async (email: any) => {
          this.logger.info('Email sent', { to: email.to, subject: email.subject });
          // In production, this would integrate with real email service
        }
      };

      this.passwordService = new PasswordService(
        this.getPersonAuthRepository(),
        this.getAuthSessionRepository(),
        this.eventBus,  // Now using real EventBus
        emailService
      );
    }
    return this.passwordService;
  }

  /**
   * Get the event bus instance (for external subscriptions)
   */
  getEventBus(): EventBus {
    return this.eventBus;
  }

  /**
   * Get health status
   */
  async getHealthStatus(): Promise<any> {
    const status = {
      healthy: false,
      database: 'disconnected',
      uptime: process.uptime(),
      memory: process.memoryUsage(),
      timestamp: new Date().toISOString()
    };

    try {
      if (this.database) {
        await this.database.admin().ping();
        status.database = 'connected';
        status.healthy = true;
      }
    } catch (error) {
      this.logger.error('Health check failed', error as Error);
    }

    return status;
  }
}